// Speicherplatz-Manager für localStorage
// Verhindert dass der Speicher jemals voll läuft durch proaktive Verwaltung

export interface StorageStats {
  used: number;
  available: number;
  total: number;
  percentage: number;
  isNearLimit: boolean;
  isCritical: boolean;
}

export interface CleanupResult {
  beforeSize: number;
  afterSize: number;
  savedBytes: number;
  itemsCleaned: string[];
}

export class StorageManager {
  private static instance: StorageManager;
  private readonly STORAGE_KEY = 'garden-clicker-storage';
  
  // Speicher-Limits (in Bytes)
  private readonly MAX_STORAGE_SIZE = 8 * 1024 * 1024; // 8MB Maximum
  private readonly WARNING_THRESHOLD = 5 * 1024 * 1024; // 5MB Warnung
  private readonly CRITICAL_THRESHOLD = 7 * 1024 * 1024; // 7MB Kritisch
  
  // Börse-spezifische Limits (1MB Maximum für Börse)
  private readonly STOCK_MARKET_MAX_SIZE = 1 * 1024 * 1024; // 1MB Maximum für Börse
  private readonly STOCK_MARKET_WARNING_THRESHOLD = 750 * 1024; // 750KB Warnung für Börse
  private readonly STOCK_MARKET_CRITICAL_THRESHOLD = 900 * 1024; // 900KB Kritisch für Börse

  private constructor() {
    // Monitoring deaktiviert - keine automatische Bereinigung
  }

  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  // Holt aktuelle Speicher-Statistiken
  public getStorageStats(): StorageStats {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      const used = data ? new Blob([data]).size : 0;
      const available = this.MAX_STORAGE_SIZE - used;
      const percentage = (used / this.MAX_STORAGE_SIZE) * 100;
      
      return {
        used,
        available,
        total: this.MAX_STORAGE_SIZE,
        percentage,
        isNearLimit: used > this.WARNING_THRESHOLD,
        isCritical: used > this.CRITICAL_THRESHOLD
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Speicher-Statistiken:', error);
      return {
        used: 0,
        available: this.MAX_STORAGE_SIZE,
        total: this.MAX_STORAGE_SIZE,
        percentage: 0,
        isNearLimit: false,
        isCritical: false
      };
    }
  }

  // Überwacht speziell die Börsen-Daten
  public getStockMarketStorageStats(): StorageStats {
    try {
      const gameData = this.getGameData();
      if (!gameData || !gameData.stockMarket) {
        return {
          used: 0,
          available: this.STOCK_MARKET_MAX_SIZE,
          total: this.STOCK_MARKET_MAX_SIZE,
          percentage: 0,
          isNearLimit: false,
          isCritical: false
        };
      }

      // Berechne Größe nur der Börsen-Daten
      const stockMarketData = JSON.stringify(gameData.stockMarket);
      const used = new Blob([stockMarketData]).size;
      const available = this.STOCK_MARKET_MAX_SIZE - used;
      const percentage = (used / this.STOCK_MARKET_MAX_SIZE) * 100;
      
      return {
        used,
        available,
        total: this.STOCK_MARKET_MAX_SIZE,
        percentage,
        isNearLimit: used > this.STOCK_MARKET_WARNING_THRESHOLD,
        isCritical: used > this.STOCK_MARKET_CRITICAL_THRESHOLD
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Börsen-Speicher-Statistiken:', error);
      return {
        used: 0,
        available: this.STOCK_MARKET_MAX_SIZE,
        total: this.STOCK_MARKET_MAX_SIZE,
        percentage: 0,
        isNearLimit: false,
        isCritical: false
      };
    }
  }

  // Bereinigungssystem deaktiviert - gibt immer null zurück
  public checkAndCleanIfNeeded(): CleanupResult | null {
    // Bereinigungssystem komplett deaktiviert
    return null;
  }

  // Hilfsfunktionen für GameStore
  private getGameData(): any {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Fehler beim Laden der Spieldaten:', error);
      return null;
    }
  }

  private saveGameData(data: any): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Fehler beim Speichern der Spieldaten:', error);
    }
  }

  private createEmptyResult(beforeSize: number): CleanupResult {
    return {
      beforeSize,
      afterSize: beforeSize,
      savedBytes: 0,
      itemsCleaned: []
    };
  }

  // Bereinigungssystem deaktiviert - gibt leeres Ergebnis zurück
  public forceCleanup(): CleanupResult {
    const beforeSize = this.getStorageStats().used;
    return this.createEmptyResult(beforeSize);
  }

  public logStorageInfo(): void {
    const stats = this.getStorageStats();
    const stockMarketStats = this.getStockMarketStorageStats();
    
    console.log('📊 Speicher-Informationen:');
    console.log(`   Gesamtspeicher: ${stats.used} / ${stats.total} Bytes (${stats.percentage.toFixed(1)}%)`);
    console.log(`   Börsen-Speicher: ${stockMarketStats.used} / ${stockMarketStats.total} Bytes (${stockMarketStats.percentage.toFixed(1)}%)`);
    console.log(`   Verfügbar: ${stats.available} Bytes`);
  }
}

export const storageManager = StorageManager.getInstance(); 